import { memo, useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  BodyComponent,
  trans,
  HeaderComponent,
  PaginationComponent,
  ImportButton,
  CreateButton, RefreshButton,
  DeleteButton,
  DownloadButton
} from "@comps/components";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { makeRequest, makeRequestStateless, handleDownloadFile } from "@utils/helpers";
import { Const } from "../constants";
import { Button, message, Popconfirm, Popover, Row, Col, Spin, Tooltip } from 'antd';
import { FilterComponent, FilterButton } from "@comps/components";
import { downloadSampleReq, getAllDataReq, getFilterReq, getTotalHoursReq } from "./requests";
import CreateCompanyRoaster from "./components/CreateCompanyRoaster";
import moment from "moment";
import { CheckOutlined, CloseOutlined, LeftOutlined, RightOutlined, RollbackOutlined, WarningOutlined } from "@ant-design/icons";
import DeleteCompanyRoaster from "./components/DeleteCompanyRoaster";
import { createSingleShift, deleteMultipleShiftsReq, getRelatedDataReq, updateMultipleShiftsReq } from "../myRoster/requests";
import CreateShift from "../myRoster/components/CreateShift";
import UpdateShift from "../myRoster/components/UpdateShift";
import ShiftCellMenu from "./components/ShiftCellMenu";
import CreateLeaveRequest from "@mods/leaveManagement/leave/companyLeaves/components/CreateLeaveRequest";
import "../common/Roster.css"
import { convertMinutesToHours, convertTimeTotz, checkPermissions, createSelectList } from '@comps/commonFunctions';
import { getDatesFilters, getOrganizationFilters, getSubordinateScopeFilters } from "../../common/commonFilterFields";
import ImportSample from "../../common/ImportSample";
import { pusherInitializer, decryptLocalData, lightenColor } from "@comps/commonFunctions";
import { useLocation } from "react-router-dom";

const langKey = Const["companyRoaster"].lngKey;
// leave screens that match each roster scope, for the "Apply Leave" permission
const leaveModkeys = {
  myRoaster: 'leaveRequest',
  subordinateRoaster: 'subordinateLeaves',
  companyRoaster: 'companyLeaveRequest',
};
const pageConfig = {
  headers: {
    title: trans(`${langKey}.moduleName`),
    breadcrumb: [
      {
        name: trans(`${langKey}.moduleName`),
        path: Const["companyRoaster"].route
      }
    ]
  }
}

const ShiftTiming = memo(({ startTime, endTime, startPeriod, endPeriod, leave }) => (
  <div>
    <div style={ {
      color: '#000',
      fontSize: 16,
    } }>
      { startTime } { " - " }
      { endTime }
    </div>
    { leave ? (
      <Tooltip title={ `${leave.leave_type_name || ''} (${leave.is_approved ? 'Approved' : leave.status})` }>
        <span className={ `roster-cell-leave ${leave.is_approved ? 'roster-cell-leave--approved' : 'roster-cell-leave--pending'}` }>
          { leave.leave_type_code || 'Leave' }{ leave.is_approved ? '' : ' · Pending' }
        </span>
      </Tooltip>
    ) : <div style={ {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingRight: 20,
      paddingLeft: 20,
      marginTop: 10,
    } }>
      <div>
        <span style={ { fontSize: 18 } }>{ startPeriod === "AM" ? "☀️" : "🌙" }</span>
      </div>
      <div>
        <span style={ { fontSize: 18 } }>{ endPeriod === "AM" ? "☀️" : "🌙" }</span>
      </div>
    </div> }
  </div>
));

const EmployeeList = memo(({ rosterGrid }) => (
  <table className="table">
    <tbody>
      { rosterGrid.map(({ employee, totalHours }) => (
        <tr key={ employee.id }>
          <td style={ {
            width: 250, overflowX: "hidden", padding: 0,
            borderBottom: '1px solid #f2f2f2',
          } }>
            <div style={ {
              height: 100,
              overflow: "hidden",
              display: 'flex',
              flexDirection: 'row',
              padding: 10,
            } }>
              <div>
                <img src={ employee.profile_pic_url !== "images/arnold-avatar.jpg" ? employee.profile_pic_url : "/images/arnold-avatar.jpg" } width={ 45 } height={ 45 } style={ { borderRadius: '60px' } } alt="" />
              </div>
              <div className="each-employment">
                <strong className="employee-full-name"> { employee.full_name } </strong>
                <p className="employee-code">{ employee.code }</p>
              </div>
              <div className="each-employment-hour">
                <p>{ totalHours }</p>
              </div>
            </div>
          </td>
        </tr>
      )) }
    </tbody>
  </table>
));

const stagedTagStyle = {
  position: 'absolute',
  top: -4,
  right: -2,
  background: '#6C5CE7',
  color: '#fff',
  fontSize: 10,
  fontWeight: 700,
  padding: '1px 5px',
  borderRadius: 6,
  lineHeight: 1.4,
  zIndex: 1,
};

const RosterCell = memo(({
  cell,
  multipleEdit,
  dateTotal,
  onEditSingleShift,
  renderActions,
}) => {
  const { date, employeeId, shift, isSelected, startTime, endTime, startPeriod, endPeriod, baseColor, pending } = cell;
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  const pendingCreate = pending?.action === 'create';
  const pendingUpdate = pending?.action === 'update';
  const pendingDelete = pending?.action === 'delete';

  const baseButtonStyle = multipleEdit
    ? {
      backgroundColor: shift
        ? (isSelected ? lightenColor('#007508', 50) : lightenColor(baseColor, 70))
        : 'rgba(234, 67, 53, 0.10)',
      color: dateTotal === 0 || shift ? '' : '#687980',
      margin: 'auto',
      width: '100%',
      height: 80,
      padding: 0,
      border: 'none',
    }
    : {
      backgroundColor: shift ? lightenColor(baseColor, 10) : 'rgba(234, 67, 53, 0.10)',
      color: dateTotal === 0 || shift ? '' : '#687980',
      margin: 'auto',
      width: '100%',
      height: 80,
      padding: 0,
      border: 'none',
    };

  // Overlay staged state: dashed border, tinted bg for new cells, dimmed for deletes.
  const buttonStyle = pending
    ? {
      ...baseButtonStyle,
      backgroundColor: pendingCreate ? '#EEF2FF' : baseButtonStyle.backgroundColor,
      border: pendingDelete ? '2px dashed #D64545' : '2px dashed #6C5CE7',
      opacity: pendingDelete ? 0.7 : 1,
    }
    : baseButtonStyle;

  let content;
  if (pendingCreate || pendingUpdate) {
    content = (
      <div style={ { position: 'relative', width: '100%' } }>
        <span style={ stagedTagStyle }>{ pendingCreate ? 'New' : 'Edited' }</span>
        <div style={ { fontWeight: 800, fontSize: 12, color: '#4338CA' } }>{ pending.timeSlotCode }</div>
        <ShiftTiming startTime={ pending.startTime } endTime={ pending.endTime } startPeriod={ pending.startPeriod } endPeriod={ pending.endPeriod } leave={ null } />
      </div>
    );
  } else if (pendingDelete) {
    content = (
      <div style={ { position: 'relative', width: '100%' } }>
        <span style={ { ...stagedTagStyle, background: '#D64545' } }>Remove</span>
        <ShiftTiming startTime={ startTime } endTime={ endTime } startPeriod={ startPeriod } endPeriod={ endPeriod } leave={ shift.leave } />
      </div>
    );
  } else if (dateTotal === 0) {
    content = <span style={ { color: "#EA4335" } }>Day Off</span>;
  } else if (shift) {
    content = multipleEdit ? (
      <div onClick={ () => onEditSingleShift(shift) }>
        <div style={ { position: 'absolute', left: 4 } }>
          { isSelected ? <CheckOutlined /> : <CloseOutlined /> }
        </div>
        <ShiftTiming startTime={ startTime } endTime={ endTime } startPeriod={ startPeriod } endPeriod={ endPeriod } leave={ shift.leave } />
      </div>
    ) : (
      <ShiftTiming startTime={ startTime } endTime={ endTime } startPeriod={ startPeriod } endPeriod={ endPeriod } leave={ shift.leave } />
    );
  } else {
    content = <span style={ { color: "#EA4335" } }>Day Off</span>;
  }

  const cellButton = (
    <Button style={ buttonStyle }>
      { content }
    </Button>
  );

  return (
    <td style={ {
      padding: 0, margin: 0, border: `1px solid #f2f2f2`
    } }>
      <div style={ {
        width: 150,
        height: 100,
        overflow: "hidden",
      } }>
        { multipleEdit ? (
          <div style={ {
            margin: 'auto',
            textAlign: 'center',
            padding: 8
          } }>
            { cellButton }
          </div>
        ) : (
          <Popover placement="bottom" title={ '' } trigger="click" open={ open } onOpenChange={ setOpen }
            overlayClassName="shift-cell-popover" content={ renderActions(date, employeeId, shift, close, pending) }>
            <div style={ {
              margin: 'auto',
              textAlign: 'center',
              padding: 8
            } }>
              { cellButton }
            </div>
          </Popover>
        ) }
      </div>
    </td>
  );
});

const RosterTable = memo(({
  dateColumns,
  rosterGrid,
  dateTotalSource,
  multipleEdit,
  onEditSingleShift,
  renderActions,
}) => (
  <table className="table">
    <thead>
      <tr>
        { dateColumns.map((date, mins) => (
          <th key={ mins } style={ { border: '1px solid #f2f2f2', padding: 14 } }>
            <div style={ {
              textAlign: 'center',
              color: "#4079FC",
            } }>
              <span style={ { marginRight: '2px' } }>{ moment(date).format('dddd').substring(0, 10) }</span>
              <span>{ moment(date).format('DD') }</span>
            </div>
          </th>
        )) }
      </tr>
    </thead>
    <tbody>
      { rosterGrid.map(({ employee, cells }) => (
        <tr key={ employee.id }>
          { cells.map((cell, mins) => (
            <RosterCell
              key={ `${cell.date}-${employee.id}-${mins}` }
              cell={ cell }
              multipleEdit={ multipleEdit }
              dateTotal={ dateTotalSource[cell.date] }
              onEditSingleShift={ onEditSingleShift }
              renderActions={ renderActions }
            />
          )) }
        </tr>
      )) }
      <tr>
        { dateColumns.map((value, mins) => (
          <td key={ value + mins }>
            <div style={ { textAlign: 'center' } }>
              <span style={ { fontSize: 16, color: '#687980' } }>{ dateTotalSource[value] ? convertMinutesToHours(dateTotalSource[value]) : '-' }</span>
            </div>
          </td>
        )) }
      </tr>
    </tbody>
  </table>
));

const IndexCompanyRoaster = (props) => {
  const location = useLocation();
  const [loader, setLoader] = useState(false);
  const [childComponent, setChildComponent] = useState(null);

  const [filters, setFilters] = useState('');
  const [employments, setEmployments] = useState([]);
  const [dateTotalSource, setDateTotalSource] = useState([]);
  const [empTotal, setempTotal] = useState({});
  const [shifts, setShifts] = useState([])
  const [fromDate, setFromDate] = useState()
  const [toDate, setToDate] = useState()
  const [editModal, setEditModal] = useState(null);
  const [loading, setloading] = useState(true)
  const [multipleEdit, setMultipleEdit] = useState(false)
  const [selectedShiftIds, setSelectedShiftIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    "total": 0,
    "count": 0,
    "per_page": 0,
    "current_page": 0,
    "total_pages": 0,
    "links": {}
  });
  const selectedShiftIdsRef = useRef([]);
  const [timeSlots, setTimeSlots] = useState([]);
  // Staged cell changes keyed by `${YYYYMMDD}-${employmentId}`; committed on Save Changes.
  const [pendingChanges, setPendingChanges] = useState({});
  // id -> { code, startTime, startPeriod, endTime, endPeriod } for previewing staged shifts.
  const [timeSlotMeta, setTimeSlotMeta] = useState({});

  const cleanFilterValue = useCallback((value = '') => {
    if (!value) {
      return '';
    }
    return value.replace('employees_|undefined;', '');
  }, []);

  const mergedFilters = useMemo(() => {
    const baseFilter = cleanFilterValue(props.filters);
    const localFilter = cleanFilterValue(filters);
    return `${baseFilter};${localFilter}`;
  }, [cleanFilterValue, props.filters, filters]);

  const dateColumns = useMemo(() => Object.keys(dateTotalSource), [dateTotalSource]);
  const selectedShiftIdSet = useMemo(() => new Set(selectedShiftIds), [selectedShiftIds]);

  const filterFields = useMemo(() => {
    const nextFilterArray = getOrganizationFilters('');

    nextFilterArray.push({
      type: "select",
      placeholder: trans(`${langKey}.time_slot_id`),
      key: "time_slot",
      data_key: "supporting_data.time_slot",
    });

    if (props.module !== 'myRoaster') {
      nextFilterArray.unshift({
        type: "select",
        placeholder: 'Employee',
        key: "employees",
        data_key: "supporting_data.employments",
        resource_type: 'employment'
      });
    }

    return nextFilterArray.concat(getDatesFilters(''));
  }, [props.module]);

  const rosterGrid = useMemo(() => {
    return employments.map((employee) => {
      const totalHours = empTotal && empTotal[employee.id] !== undefined
        ? convertMinutesToHours(parseFloat(empTotal[employee.id]))
        : '';

      const cells = dateColumns.map((date) => {
        const dateKey = `${moment(date).format('YYYYMMDD')}-${employee.id}`;
        const shift = shifts?.[dateKey]?.[0] || null;
        const isSelected = shift ? selectedShiftIdSet.has(shift.id) : false;
        const pending = pendingChanges[dateKey] || null;

        if (!shift) {
          return {
            date,
            employeeId: employee.id,
            shift: null,
            isSelected: false,
            startTime: '',
            endTime: '',
            startPeriod: '',
            endPeriod: '',
            baseColor: 'rgba(234, 67, 53, 0.10)',
            pending,
          };
        }

        const start = convertTimeTotz(shift.start);
        const end = convertTimeTotz(shift.end);

        return {
          date,
          employeeId: employee.id,
          shift,
          isSelected,
          startTime: start.format('hh:mm'),
          endTime: end.format('hh:mm'),
          startPeriod: start.format('A'),
          endPeriod: end.format('A'),
          baseColor: shift.bg_colour,
          pending,
        };
      });

      return {
        employee,
        totalHours,
        cells,
      };
    });
  }, [dateColumns, employments, empTotal, selectedShiftIdSet, shifts, pendingChanges]);

  const pendingCount = useMemo(() => Object.keys(pendingChanges).length, [pendingChanges]);


  // ... (other code)
  // ==================================
  //   Use States End
  // ==================================

  // ================================== 
  //   Table Data End
  // ==================================
  //==========================Start Pusher=================================
  //==========================end Pusher =================================

  useEffect(() => {
    setFilters('')
    // eslint-disable-next-line
  }, [props]);

  // ==================================
  //   Table Data End
  // ==================================
  const getEmployments = useCallback((id) => {
    void id;
  }, []);

  const onSuccess = useCallback((response) => {

    setPagination(response.pagination);
    setChildComponent(null);
    setEditModal(null)
    setEmployments(response.employments);
    setFromDate(response.from_date)
    setToDate(response.to_date)

    //   Object.values(response.shifts).forEach(each => {
    //     each[0]['selected']=false;
    // });

    setShifts(response.shifts)

    let empids = ''
    response.employments.forEach((each) => {
      empids += `${each.id},`
    })
    const payload = {
      page: currentPage,
      filters: mergedFilters,
      empIds: empids
    }
    makeRequest(setLoader, getTotalHoursReq, payload, onGetTotalHours, null);
  }, [currentPage, mergedFilters]);

  const getAllData = useCallback(() => {
    setloading(true)
    selectedShiftIdsRef.current = [];
    setSelectedShiftIds([]);
    setPendingChanges({});
    const payload = {
      page: currentPage,
      filters: mergedFilters
    }
    makeRequest(setLoader, getAllDataReq, payload, onSuccess, onErrorCreatingRoaster);
  }, [currentPage, mergedFilters, onSuccess]);

  useEffect(() => {
    const user = decryptLocalData('user');
    if (!user || !user.tenant) {
      return;
    }

    const pusher = pusherInitializer();
    const channel = pusher.subscribe(user.tenant);
    const refreshRoster = () => {
      // getAllData();
      // getEmployments();
    };

    channel.bind('shift-creation-errors-event', refreshRoster);
    channel.bind('shifts-created-event', refreshRoster);

    return () => {
      channel.unbind('shift-creation-errors-event', refreshRoster);
      channel.unbind('shifts-created-event', refreshRoster);
      pusher.unsubscribe(user.tenant);
      pusher.disconnect();
    };
  }, [getAllData, getEmployments]);

  useEffect(() => {
    getAllData();
    getEmployments();
    setMultipleEdit(false);
  }, [location.pathname, getAllData, getEmployments]);
  useEffect(() => {
    makeRequestStateless(getRelatedDataReq, null, (response) => {
      const slots = response.time_slots || [];
      setTimeSlots(createSelectList(slots));
      const meta = {};
      slots.forEach((slot) => {
        meta[slot.id] = {
          code: slot.code || slot.name,
          startTime: slot.start_time || '',
          startPeriod: slot.start_period || '',
          endTime: slot.end_time || '',
          endPeriod: slot.end_period || '',
        };
      });
      setTimeSlotMeta(meta);
    }, () => { setTimeSlots([]); setTimeSlotMeta({}); });
  }, []);
  const onErrorCreatingRoaster = (err, res) => {
  }
  const onGetTotalHours = (response) => {
    setDateTotalSource(response.date_total)
    setempTotal(response.emp_total)
    // setLoader(false)
    setloading(false)
  }

  const cancel = (e) => {

  };
  const onDeleted = useCallback((res) => {
    message.success("Record Deleted")
    getAllData();
  }, [getAllData]);
  const onError = useCallback((err, res) => {
    message.info(res.data.message);
    getAllData();
    //setErrors(errorList);
  }, [getAllData])
  const cellKeyOf = useCallback((date, employmentId) => `${moment(date).format('YYYYMMDD')}-${employmentId}`, []);

  // ---- Staged cell edits (committed together on "Save Changes") ----
  // Pick a time slot: stage a create (empty cell) or update (existing shift).
  // Choosing the shift's current slot back cancels the change.
  const stageSelectTimeSlot = useCallback((date, employmentId, shift, time_slot_id) => {
    const key = cellKeyOf(date, employmentId);
    const slot = timeSlots.find((s) => s.value === time_slot_id);
    const timeSlotName = slot ? slot.label : '';
    const meta = timeSlotMeta[time_slot_id] || {};
    const preview = {
      timeSlotName,
      timeSlotCode: meta.code || timeSlotName,
      startTime: meta.startTime || '',
      startPeriod: meta.startPeriod || '',
      endTime: meta.endTime || '',
      endPeriod: meta.endPeriod || '',
    };
    setPendingChanges((prev) => {
      const next = { ...prev };
      if (shift) {
        if (shift.time_slot_id === time_slot_id) {
          delete next[key];
        } else {
          next[key] = {
            action: 'update', date, employmentId,
            shiftId: shift.id, timeSlotId: time_slot_id,
            ...preview,
            is_full_overtime_shift: !!shift.is_full_overtime_shift,
          };
        }
      } else {
        next[key] = { action: 'create', date, employmentId, timeSlotId: time_slot_id, ...preview };
      }
      return next;
    });
  }, [cellKeyOf, timeSlots, timeSlotMeta]);

  // "Mark as Day Off" on an existing shift: stage a delete (reversible until save).
  const stageDeleteShift = useCallback((date, employmentId, shift) => {
    const key = cellKeyOf(date, employmentId);
    setPendingChanges((prev) => ({
      ...prev,
      [key]: { action: 'delete', date, employmentId, shiftId: shift.id },
    }));
  }, [cellKeyOf]);

  // Drop a cell's staged change and restore its saved state.
  const onUndoCell = useCallback((date, employmentId) => {
    const key = cellKeyOf(date, employmentId);
    setPendingChanges((prev) => {
      if (!(key in prev)) {
        return prev;
      }
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, [cellKeyOf]);

  const onDiscardChanges = useCallback(() => {
    setPendingChanges({});
    message.info('Pending changes discarded');
  }, []);

  // Commit every staged change in as few requests as possible, then reload once:
  // one batch delete, updates grouped by target slot, and a create per new cell.
  const onSaveChanges = useCallback(async () => {
    const entries = Object.values(pendingChanges);
    if (!entries.length) {
      return;
    }
    const scopedFilters = `${props.filters};${filters}`;
    setLoader(true);

    const deleteIds = entries.filter((e) => e.action === 'delete').map((e) => e.shiftId);
    const updates = entries.filter((e) => e.action === 'update');
    const creates = entries.filter((e) => e.action === 'create');

    let failed = 0;
    const ok = (res) => res && (res.code === 200 || res.success === true);
    const track = (promise) => promise.then((res) => { if (!ok(res)) failed += 1; }).catch(() => { failed += 1; });

    const calls = [];

    if (deleteIds.length) {
      calls.push(track(deleteMultipleShiftsReq({ shift: deleteIds })));
    }

    // editShiftsMultiple sets one time slot for all ids, so group by slot + overtime flag.
    const updateGroups = {};
    updates.forEach((e) => {
      const ot = e.is_full_overtime_shift ? 1 : 0;
      const gKey = `${e.timeSlotId}|${ot}`;
      if (!updateGroups[gKey]) {
        updateGroups[gKey] = { ids: [], time_slot_id: e.timeSlotId, is_full_overtime_shift: !!e.is_full_overtime_shift };
      }
      updateGroups[gKey].ids.push(e.shiftId);
    });
    Object.values(updateGroups).forEach((g) => {
      calls.push(track(updateMultipleShiftsReq({
        shift: { ids: g.ids, time_slot_id: g.time_slot_id, is_full_overtime_shift: g.is_full_overtime_shift },
        filters: scopedFilters,
        page: currentPage,
      })));
    });

    creates.forEach((e) => {
      calls.push(track(createSingleShift({
        shift: { date: e.date, time_slot_id: e.timeSlotId, employment_id: e.employmentId },
        filters: scopedFilters,
      })));
    });

    await Promise.all(calls);
    setPendingChanges({});
    if (failed) {
      message.warning(`Saved with ${failed} issue${failed > 1 ? 's' : ''}. Please review the roster.`);
    } else {
      message.success('Changes saved');
    }
    getAllData();
  }, [pendingChanges, props.filters, filters, currentPage, getAllData]);

  // Create component modal
  const onDeleteAll = () => {
    setChildComponent(<DeleteCompanyRoaster onCreated={ onCreated } employments={ employments } page={ currentPage } />);
  };
  const onCreate = () => {
    setChildComponent(<CreateCompanyRoaster onCreated={ onCreated } onSuccess={ onSuccess } page={ currentPage } />);
  };

  const onCreated = useCallback((data) => {
    if (data) {
      setEmployments(data.employments);
      setShifts(data.shifts)
      getAllData()
    }

    setChildComponent(null);
    setEditModal(null)
  }, [getAllData]);
  const onCreateSingleShift = useCallback((date, employment_id) => {
    setEditModal(
      <CreateShift onCreated={ onCreated } date={ date } employment_id={ employment_id } employments={ employments } getAllData={ getAllData } filters={ `${props.filters};${filters}` } page={ currentPage } />
    );
  }, [currentPage, employments, filters, getAllData, onCreated, props.filters]);

  const onEnableDisableMultiple = () => {
    if (!multipleEdit && pendingCount > 0) {
      message.warning('Save or discard your pending changes before using multiselect.');
      return;
    }
    setMultipleEdit(!multipleEdit);
    selectedShiftIdsRef.current = [];
    setSelectedShiftIds([]);
  }
  const onEditSingleShift = useCallback((shift) => {
    if (multipleEdit) {
      const exists = selectedShiftIdsRef.current.includes(shift.id);
      const nextSelectedShiftIds = exists
        ? selectedShiftIdsRef.current.filter((id) => id !== shift.id)
        : [...selectedShiftIdsRef.current, shift.id];

      selectedShiftIdsRef.current = nextSelectedShiftIds;
      setSelectedShiftIds(nextSelectedShiftIds);
    }
    else {
      setEditModal(
        <UpdateShift onCreated={ onCreated } shift={ shift } getAllData={ getAllData } multiple={ false } filters={ `${props.filters};${filters}` } page={ currentPage } />
      );
    }
  }, [currentPage, filters, getAllData, multipleEdit, onCreated, props.filters]);

  const onEditMultipleShift = () => {
    setEditModal(
      <UpdateShift onCreated={ onCreated } shift={ null } getAllData={ getAllData } multiple={ true } edit={ true } shiftIds={ selectedShiftIds } filters={ `${props.filters};${filters}` } page={ currentPage } />
    );
  }
  const onDeleteMultipleShifts = () => {
    let payload = {
      shift: selectedShiftIds
    }
    makeRequest(setLoader, deleteMultipleShiftsReq, payload, onDeleted, onError);
  }



  //download sample log
  const onDownloadSample = () => {
    makeRequestStateless(downloadSampleReq, null, onSuccessDownloadSample, onSuccessDownloadSample);
  }
  const onSuccessDownloadSample = (res, err) => {
    if (res.code === undefined) {
      // Extract file extension
      const fileExtension = res.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ? 'xlsx' : 'pdf'
      const fileName = `roster_sample_import.${fileExtension}`
      handleDownloadFile(fileName, res)
    }
    else {
      message.error(res.message)
    }
  }

  const onApplyLeave = useCallback((shift, leave_type_id) => {
    const onLeaveSaved = (data) => {
      if (data !== false) {
        getAllData();
      }
      setEditModal(null);
    };
    setEditModal(
      <CreateLeaveRequest source="roster" record={ { date: shift.date } } employment_id={ shift.employment_id }
        leave_type_id={ leave_type_id } autoApprove={ true } filters={ props.filters } onCompleted={ onLeaveSaved } />
    );
  }, [getAllData, props.filters]);

  const renderActions = useCallback((date, employment_id, shift, close, pending) => {
    const withClose = (fn) => (...args) => {
      close();
      fn(...args);
    };
    const undoButton = pending ? (
      <Button type="text" block className="shift-cell-menu__item" icon={ <RollbackOutlined /> } onClick={ withClose(() => onUndoCell(date, employment_id)) }>
        <span className="shift-cell-menu__label">Undo change</span>
      </Button>
    ) : null;
    const actions = shift ? (
      <>
        { undoButton }
        <Button type="text" block className="shift-cell-menu__item shift-cell-menu__item--danger" icon={ <DeleteOutlined /> }
          disabled={ !checkPermissions(null, 'delete') || pending?.action === 'delete' }
          onClick={ withClose(() => stageDeleteShift(date, employment_id, shift)) }>
          <span className="shift-cell-menu__label">Mark as Day Off</span>
        </Button>
      </>
    ) : (
      <>
        { undoButton }
        <Button type="text" block className="shift-cell-menu__item" icon={ <PlusOutlined className="text-primary" /> } onClick={ withClose(() => onCreateSingleShift(date, employment_id)) } disabled={ !checkPermissions(null, 'create') }>
          <span className="shift-cell-menu__label">Shift</span>
        </Button>
      </>
    );
    const selectedTimeSlotId = pending
      ? (pending.action === 'delete' ? null : pending.timeSlotId)
      : (shift ? shift.time_slot_id : null);
    return (
      <ShiftCellMenu
        employmentId={ employment_id }
        shift={ shift }
        actions={ actions }
        timeSlots={ timeSlots }
        selectedTimeSlotId={ selectedTimeSlotId }
        canAssignTimeSlot={ checkPermissions(null, shift ? 'update' : 'create') }
        canApplyLeave={ checkPermissions(leaveModkeys[props.module], 'create') }
        onSelectTimeSlot={ withClose((time_slot_id) => stageSelectTimeSlot(date, employment_id, shift, time_slot_id)) }
        onApplyLeave={ withClose((leave_type_id) => onApplyLeave(shift, leave_type_id)) }
      />
    );
  }, [onApplyLeave, onCreateSingleShift, stageDeleteShift, stageSelectTimeSlot, onUndoCell, props.module, timeSlots]);
  //---------------filters-----------------------
  const onOpenFilter = () => {
    setChildComponent(<FilterComponent filters={ filterFields } modkey={ props.modkey } selected={ filters } api={ getFilterReq } optionScopeFilters={ getSubordinateScopeFilters(props.filters) } setCurrentPage={ setCurrentPage } onFilter={ setFilters } onCloseFilter={ onCloseFilter }
    />
    )
  }

  const onCloseFilter = () => {
    setChildComponent(null);
  }
  //----end filter--------------------
  const onClickBackDate = (start, end) => {
    message.info("Loading shifts ...")
    setloading(true)
    const from = moment(start).subtract(1, 'months').format('YYYY-MM-DD');
    const to = moment(end).subtract(1, 'months').format('YYYY-MM-DD');
    const payload = `${filters};from_|${from};to_|${to};`
    setLoader(true)
    setFilters(payload)
  }

  const onClickNextDate = (start, end) => {
    message.info("Loading shifts ...")
    setloading(true)
    const from = moment(start).add(1, 'months').format('YYYY-MM-DD');
    const to = moment(end).add(1, 'months').format('YYYY-MM-DD');
    const payload = `${filters};from_|${from};to_|${to};`
    setLoader(true)
    setFilters(payload)
  }

  const onRefresh = () => {
    getAllData()
    setloading(true)
    getEmployments()
  }
  return (
    <>

      { childComponent }
      <BodyComponent>

        <HeaderComponent className="header-component" headers={ pageConfig.headers }>
          <RefreshButton onClick={ onRefresh } />
          {

            props.module !== 'myRoaster' && <>
              <DeleteButton onClick={ onDeleteAll } disabled={ !checkPermissions(null, 'delete') } />
              <DownloadButton onClick={ onDownloadSample } />
              <ImportButton onClick={ () => setChildComponent(<ImportSample onSuccess={ getAllData } api="roster/import" module="Roaster" onCompleted={ () => setChildComponent(null) } />) } disabled={ !checkPermissions(null, 'create') } />
              <CreateButton onClick={ onCreate } disabled={ !checkPermissions(null, 'create') } />
            </>
          }

          {
            props.module !== 'myRoaster' && <FilterButton onClick={ onOpenFilter } />
          }
          {/* <MultiselectButton className="multiselect-button" />
          <ImportButton className="import-button" style={ { position: 'absolute', top: 60, right: 172 } } /> */}


        </HeaderComponent>

        <div style={ {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'right',
          marginTop: '1%',
          marginBottom: '2%'
        } }>
          { !multipleEdit && pendingCount > 0 &&
            <div style={ { display: 'flex', alignItems: 'center', gap: 10, marginRight: 10 } }>
              <span style={ { fontWeight: 600, color: '#6C5CE7' } }>
                { pendingCount } unsaved change{ pendingCount > 1 ? 's' : '' }
              </span>
              <Popconfirm
                icon={ <WarningOutlined style={ { color: 'red' } } /> }
                title="Discard changes"
                description="Discard all unsaved roster changes?"
                onConfirm={ onDiscardChanges }
                onCancel={ cancel }
                okText="Yes"
                cancelText="No"
              >
                <Button>Discard</Button>
              </Popconfirm>
              <CreateButton onClick={ onSaveChanges } text="Save Changes" className="bg-success" />
            </div>
          }
          { multipleEdit && selectedShiftIds.length > 0 &&
            <>
              <Popconfirm style={ { width: 590, height: 140 } }
                icon={ <WarningOutlined style={ { color: 'red' } } /> }
                title="Delete"
                description="Are you sure to delete selected shifts?"
                onConfirm={ () => onDeleteMultipleShifts() }
                onCancel={ cancel }
                okText="Yes"
                cancelText="No"
              >
                <DeleteButton disabled={ !checkPermissions(null, 'delete') } style={ {
                  marginRight: 10
                } } text={ "Delete Multiple" } />
              </Popconfirm>

              <CreateButton disabled={ !checkPermissions(null, 'create') } style={ {
                marginRight: 10
              } } onClick={ () => onEditMultipleShift() } text={ "Edit Multiple" } className={ "bg-info" } />
            </>
          }
          { checkPermissions(props.modkey, 'create') && <CreateButton onClick={ onEnableDisableMultiple } text={ multipleEdit ? "Disable Multiselect" : "Enable Multiselect" } className={ multipleEdit ? 'bg-danger' : "bg-success" } /> }

        </div>


        <div style={ {
          marginTop: '2%'
        } }>
          {/* <Spin spinning={ loader }> */ }
          { editModal }
          <div style={ {
            background: '#FFFFFF',
            paddingTop: 30,
            border: '1px solid #C9D6DF',
            borderRadius: 10,
          } }>
            <Spin spinning={ loading || loader } tip="Loading">
              <Row>
                {/* <Col span={ 5 } style={ {
                  paddingLeft: 20,
                } }>
                  { props.module !== 'myRoaster' &&
                    <SelectWithSearch className="search-employee"
                      loading={ loader }
                      style={ { minWidth: 282, borderRadius: 5, background: '#FFFFFF' } }
                      placeholder="Search"
                      size='large'
                      options={ createEmploymentResource(employees) }
                      onChange={onChangeEmployment}
                      onSearch={ (id) => getEmployments(id) }
                    />
                  }
                </Col> */}
                <Col span={ 19 } style={ {
                  textAlign: 'left',
                  paddingBottom: 20,
                  fontSize: 20,
                  marginLeft: 20
                } }>

                  <Tooltip placement="top" title="Previous Month">
                    <Button style={ { color: '#000', border: 'none', marginLeft: -15, height: 20, background: 'none' } } icon={ <LeftOutlined /> } onClick={ () => onClickBackDate(fromDate, toDate) } />
                  </Tooltip>
                  <span style={ {
                    color: "#000",
                    fontWeight: "bold",
                    marginLeft: 10,
                    marginRight: 10,
                  } }>
                    { moment(fromDate).format('DD MMMM YYYY') } { " - " }
                    { moment(toDate).format('DD MMMM YYYY') }
                  </span>
                  <Tooltip placement="top" title="Next Month">
                    <Button style={ { color: "#000", border: 'none', height: 20, background: 'none' } } icon={ <RightOutlined /> } onClick={ () => onClickNextDate(fromDate, toDate) } />
                  </Tooltip>
                </Col>
              </Row>
              <Row>
                <Col span={ 5 } style={ {
                  paddingTop: 51,
                } }>
                  <EmployeeList rosterGrid={ rosterGrid } />
                </Col>
                <Col span={ 19 } style={ {
                  overflowX: "scroll",
                } }>
                  <RosterTable
                    dateColumns={ dateColumns }
                    rosterGrid={ rosterGrid }
                    dateTotalSource={ dateTotalSource }
                    multipleEdit={ multipleEdit }
                    onEditSingleShift={ onEditSingleShift }
                    renderActions={ renderActions }
                  />
                </Col>
              </Row>
            </Spin>
          </div>
          {/* </Spin> */ }
        </div >



      </BodyComponent >
      <PaginationComponent pagination={ pagination } modkey={ props.modkey } setCurrentPage={ setCurrentPage } currentPage={ currentPage } />
    </>
  );
};
export default IndexCompanyRoaster;
